<template>
    <div class="msg-card">
        <div class="msg-card-header">
            <span class="msg-card-title">{{ $t('common.messages') }}</span>
            <span v-if="!read && !loading" class="msg-badge">1</span>
        </div>

        <skeleton-loader v-if="loading" type="chat-list" :count="1" />
        <div v-else class="msg-preview" v-ripple @click="showChat">
            <student-avatar :avatar="clientImg" :name="clientName" :avatar-key="avatarKey" :size="40" :radius="20" />
            <div class="msg-info">
                <div class="msg-name text-truncate">{{ clientName }}</div>
                <div class="msg-text text-truncate">{{ message }}</div>
            </div>
        </div>

        <chat-dialog v-if="studentId" :clientId="studentId" ref="chat"></chat-dialog>
    </div>
</template>

<script>
import { findChat } from "@/utils/Messages";
import ChatDialog from "@/components/student/ChatDialog";
import StudentAvatar from "@/components/common/StudentAvatar";
import SkeletonLoader from "@/components/common/SkeletonLoader";

export default {
    name: "RecentChatProfile",
    components: { SkeletonLoader, StudentAvatar, ChatDialog },
    props: {
        student: {
            type: Object,
            default: null,
        }
    },
    data() {
        return {
            loading: false,
            lastMessage: null,
        }
    },
    computed: {
        studentId(vm = this) {
            return vm.student?.id || null
        },
        coachId(vm = this) {
            return vm.currentUser.uid || null
        },
        isMyLastMessage(vm = this) {
            return vm.lastMessage?.from === vm.coachId || false
        },
        read(vm = this) {
            if (vm.lastMessage && !vm.isMyLastMessage) return vm.lastMessage?.read
            return true
        },
        clientImg(vm = this) {
            return (vm.isMyLastMessage ? vm.account?.avatar : vm.student?.avatar) || null
        },
        avatarKey(vm = this) {
            const key = vm.isMyLastMessage ? (vm.account?.id || vm.account?.name) : (vm.studentId || vm.clientName)
            return key || ''
        },
        clientName(vm = this) {
            return vm.isMyLastMessage ? vm.account?.name : vm.student?.name
        },
        message(vm = this) {
            return vm.lastMessage?.message || ''
        }
    },
    async mounted() {
        const vm = this
        await vm.loadLastMessage()
    },
    methods: {
        showChat() {
            const vm = this
            vm.$refs.chat.show()
        },
        async loadLastMessage() {
            const vm = this
            if (!vm.coachId || !vm.studentId) return
            await vm.withLoading(async () => {
                const chat = await findChat('privateChats', 'coach_student', vm.coachId, vm.studentId)
                vm.lastMessage = chat?.data()?.lastMessage || null
            })
        },
    },
    watch: {
        account() {
            this.loadLastMessage()
        }
    }
}
</script>

<style scoped>
.msg-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
}

.msg-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid var(--v-outline-base);
}

.msg-card-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.msg-badge {
    background: var(--v-accent-base);
    color: #FFFFFF;
    font-size: 10px;
    font-weight: 700;
    border-radius: 10px;
    padding: 1px 7px;
}

.msg-preview {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    cursor: pointer;
    transition: background 0.15s;
}

.msg-preview:hover {
    background: var(--v-surface2-base);
}

.msg-info {
    flex: 1;
    min-width: 0;
}

.msg-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.msg-text {
    margin-top: 2px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}
</style>
